/** @forge-header
 * @id          esportsParser
 * @role        Esports ticker parser | CS2/Dota2/Valorant/LoL game moneylines
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,TennisMatchInfo | kalshiTicker/helpers:parseFallback,splitAnchoredCompetitors
 * @downstream  kalshiTicker/parse
 * @exports     parseEsportsGame:fn
 * @test        parse.test.ts (indirect)
 */

import type { ParsedTicker, TennisMatchInfo } from '../types';
import { parseDateFromTicker } from '../types';
import { parseFallback, splitAnchoredCompetitors, isDrawOutcome } from '../helpers';

// Title shown in labels, keyed by the game series prefix.
const ESPORTS_TITLES: Array<[string, string]> = [
  ['KXCS2GAME', 'CS2'],
  ['KXDOTA2GAME', 'Dota 2'],
  ['KXVALORANTGAME', 'Valorant'],
  ['KXLOLGAME', 'LoL'],
];

/**
 * Esports game: KXCS2GAME-26MAY281700IMPSHIN-IMP
 * Team-vs-team moneyline with an HHMM start-time prefix after the date and
 * variable-length org codes. One parser handles every esports title; the
 * title is derived from the matched series prefix. Org codes are displayed
 * as-is (uppercase) — they're acronyms (FNC, NAVI, OG, LGD), not names.
 *
 * The tie leg (`…-TIE`) is guarded EXPLICITLY rather than left to fall through
 * the split (#837). Its outcome bounds neither org, so the pack has no proven
 * cut and the matchup is left unresolved — a `1WIN`/`FLC` pack midpointed to
 * `1WI`/`NFLC` while both sibling markets read it correctly. Unlike soccer,
 * esports has no producer-side recovery: hydra stamps no canonical identity on
 * these frames at all (no canonicalAwayCode/HomeCode/Teams/GameKeyV2/yesOutcome,
 * measured 32/32 on hc1, 2026-09-09), so a tie leg renders its exact ticker and
 * "Tie" until that upstream gap is closed.
 */
export function parseEsportsGame(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) return parseFallback(raw);

  const entry = ESPORTS_TITLES.find(([prefix]) => raw.startsWith(prefix));
  const series = entry?.[0] ?? 'KXESPORTS';
  const title = entry?.[1] ?? 'Esports';

  const mid = parts[1] ?? '';
  if (mid.length < 9) return parseFallback(raw);

  const date = parseDateFromTicker(mid.slice(0, 7));
  const outcome = parts[2] ?? null;
  const pair = splitAnchoredCompetitors(mid.slice(7), outcome);

  const fight: TennisMatchInfo | null = pair
    ? {
        player1: { playerCode: pair[0], displayName: pair[0] },
        player2: { playerCode: pair[1], displayName: pair[1] },
      }
    : null;

  const outcomeLabel = isDrawOutcome(outcome) ? 'Tie' : outcome ? `${outcome} Win` : null;
  const matchup = pair ? `${pair[0]} vs ${pair[1]}` : raw;
  const shortLabel = outcomeLabel ? `${title}: ${outcomeLabel}` : title;
  const fullLabel = `${title}: ${matchup}${outcomeLabel ? ` · ${outcomeLabel}` : ''}`;

  return {
    raw,
    category: 'esports-game',
    series,
    date,
    game: null,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    fight,
    outcome,
    shortLabel,
    fullLabel,
  };
}
